import esriConfig from "@arcgis/core/config";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useWindowSize from "../../hooks/useWindowSize";
import { setPrintWidgetLoading } from "../../redux/action/PrintWidget-action";
import { view } from "../../utils/API";
import { defaultMap, symbolMap } from "../../utils/symbologies";
import { layoutDimensions } from "./helpers";
import { StyledPrintMask } from "./PrintMask-styled";
import PrintScreen from "./PrintScreen/PrintScreen";

const PrintMask = ({ parent, config }) => {
  const windowSize = useWindowSize();

  const { t, i18n } = useTranslation("common");
  const printWidget = useSelector((state) => state.printWidget);

  const [symbologyVariations, setSymbologyCombinations] = useState({});
  const dispatch = useDispatch();

  const [page, setPage] = useState({
    size: "A4",
    orientation: "Landscape",
  });

  const [dpi, setDpi] = useState("300");

  const addMask = () => {
    const value = `LogIE_LogisticsCluster_${page.size}_${page.orientation}`;

    const printOutDimensions = layoutDimensions[value];
    if (printOutDimensions) {
      const offsetWidth = windowSize.width;
      const printoutRatio = printOutDimensions[0] / printOutDimensions[1];
      const offsetHeight = parent.offsetHeight - 68;
      const screenRatio = offsetWidth / offsetHeight;

      // Vertically cut? if true, we cut from the top and button, otherwise from the left and right
      const verticalCut = printoutRatio > screenRatio;

      let calculatedWidth = offsetHeight * printoutRatio;

      let calculatedMaskWidth = (offsetWidth - calculatedWidth) / 2;
      let printPreviewHeight;
      if (calculatedMaskWidth < 260) {
        const reduceLeftMaskByPx = 260 - calculatedMaskWidth;
        calculatedMaskWidth = calculatedMaskWidth - reduceLeftMaskByPx;

        const offset = page.orientation === "Landscape" ? 114 : 37;
        calculatedMaskWidth =
          calculatedMaskWidth > offset ? calculatedMaskWidth : offset;

        calculatedWidth = offsetWidth - calculatedMaskWidth - 260;

        if (calculatedMaskWidth === offset) {
          printPreviewHeight = calculatedWidth / printoutRatio;
        }
      }

      return {
        type: "horizontal",
        style: {
          width: calculatedMaskWidth,
        },
        width: calculatedWidth,
        height: printPreviewHeight,
      };
    }
  };

  const masks = useMemo(() => addMask(), [page, windowSize]);

  function findSymbols(
    obj,
    results = {
      symbols: [],
      defaultSymbols: [],
    }
  ) {
    if (typeof obj !== "object" || obj === null) {
      return results;
    }

    // Check if the current object has a `symbol` property
    if (obj.hasOwnProperty("symbol")) {
      if (obj.symbol.type === "esriPMS") {
        results.symbols.push(obj.symbol);
      }
    }

    if (obj.hasOwnProperty("defaultSymbol")) {
      if (obj.defaultSymbol.type === "esriPMS") {
        results.defaultSymbols.push(obj.defaultSymbol);
      }
    }

    // Recursively check all properties of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        findSymbols(obj[key], results);
      }
    }

    return results;
  }

  async function convertSvgBase64ToPng(svgBase64, width, height) {
    return new Promise((resolve, reject) => {
      // Create an Image object
      const img = new Image();

      // Set the source of the image to the SVG base64 string
      img.src = `${svgBase64}`;

      img.onload = () => {
        // Create a canvas element
        const canvas = document.createElement("canvas");
        canvas.width = width || img.width; // Use provided width or the SVG's width
        canvas.height = height || img.height; // Use provided height or the SVG's height

        // Draw the SVG onto the canvas
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Convert the canvas content to a PNG base64 string
        const pngBase64 = canvas.toDataURL("image/png");

        // Resolve the promise with the PNG base64 string
        resolve(pngBase64);
      };

      img.onerror = (err) => {
        console.error("Error loading SVG image:", err);
        reject(err);
      };
    });
  }

  const filterUniqueValueInfos = ({ layer, layerCombinations }) => {
    const filteredUniqueValueInfos =
      layer.layerDefinition.drawingInfo.renderer.uniqueValueInfos.filter(
        (info) => {
          const value = info.value;

          // Check if the value is a range
          if (typeof value === "string" && value.includes("-")) {
            const [min, max] = value.split("-").map(Number); // Parse the range
            if (!isNaN(min) && !isNaN(max)) {
              // Check if any value in the set falls within the range
              for (const setValue of layerCombinations) {
                const numericSetValue = Number(setValue);
                if (
                  !isNaN(numericSetValue) &&
                  numericSetValue >= min &&
                  numericSetValue <= max
                ) {
                  return true;
                }
              }
            }
            return false; // No matching value in the range
          }

          // For non-range values, check if the set contains the value
          return layerCombinations.has(value);
        }
      );

    return filteredUniqueValueInfos;
  };

  const timer = useRef(0);

  //intercept request for main map and add extent
  useEffect(() => {
    const allLayersVisibilityMap = new Map();

    view.map.allLayers.forEach((layers) => {
      allLayersVisibilityMap.set(layers.id, layers.visible);
    });

    esriConfig.request.interceptors.push({
      urls: config.printServiceUrl + "/submitJob",
      before: async function (params) {
        if (params.requestOptions.query.Layout_Template.includes("Zoom")) {
          const printMask = document.querySelector("#print-mask");

          if (printMask) {
            const combinations = printMask.getAttribute("data-combinations");

            let jsonToEdit = JSON.parse(
              params.requestOptions.query["Web_Map_as_JSON"]
            );
            const symbologyCombinations = deserializeCombinations(combinations);
            jsonToEdit.operationalLayers.forEach((layer) => {
              if (
                !layer.showLabels &&
                Array.isArray(layer?.layerDefinition?.drawingInfo?.labelingInfo)
              ) {
                layer.layerDefinition.drawingInfo.labelingInfo = [];
              }

              if (layer?.layerDefinition?.drawingInfo?.renderer) {
                if (
                  layer.layerDefinition.drawingInfo.renderer.uniqueValueGroups
                ) {
                  layer.layerDefinition.drawingInfo.renderer.uniqueValueGroups =
                    [];
                }

                // Get the combinations set for this layer
                const layerCombinations = symbologyCombinations[layer.id];

                if (
                  layerCombinations &&
                  layer.layerDefinition.drawingInfo.renderer.uniqueValueInfos
                ) {
                  // Filter uniqueValueInfos to keep only items with values in the combinations set
                  layer.layerDefinition.drawingInfo.renderer.uniqueValueInfos =
                    filterUniqueValueInfos({ layer, layerCombinations });
                }
              }
            });
            params.requestOptions.query["Web_Map_as_JSON"] =
              JSON.stringify(jsonToEdit);
          }
          // clearTimeout(timer.current);

          // dispatch(setPrintWidgetLoading(true));
          return;
        }

        const printPreview = document.querySelector("#printPreview");
        const printMask = document.querySelector("#print-mask");

        const dataPage = printPreview?.getAttribute("data-page");
        const dpi = printPreview?.getAttribute("data-dpi");
        const combinations =
          printMask?.getAttribute("data-combinations") || `{}`;

        if (!printPreview) {
          return;
        }

        const value = `LogIE_LogisticsCluster_${dataPage}`;

        const printOutDimensions = layoutDimensions[value];

        if (!printOutDimensions) return;

        const parent = document.getElementsByClassName("esri-view-root");

        if (!parent[0]?.parentNode?.id === "viewDiv") return;

        const screen = parent[0];
        const printoutRatio = printOutDimensions[0] / printOutDimensions[1];
        const screenRatio = screen.offsetWidth / screen.offsetHeight;

        // Vertically cut? if true, we cut from the top and button, otherwise from the left and right
        const verticalCut = printoutRatio > screenRatio;
        const symbologyCombinations = deserializeCombinations(combinations);
        let jsonToEdit = JSON.parse(
          params.requestOptions.query["Web_Map_as_JSON"]
        );

        let locationsGraphicsLayer;
        if (dataPage?.includes("A0")) {
          locationsGraphicsLayer = view.map.layers.find(
            (l) => l.id === "locationsGraphicsLayer"
          );
        }

        for (const layer of jsonToEdit.operationalLayers) {
          if (layer.layerDefinition && layer.layerDefinition.featureReduction) {
            layer.layerDefinition.featureReduction = null;
          }

          if (
            !layer.showLabels &&
            Array.isArray(layer?.layerDefinition?.drawingInfo?.labelingInfo)
          ) {
            layer.layerDefinition.drawingInfo.labelingInfo = [];
          }

          if (layer?.layerDefinition?.drawingInfo?.renderer) {
            if (layer.layerDefinition.drawingInfo.renderer.uniqueValueGroups) {
              layer.layerDefinition.drawingInfo.renderer.uniqueValueGroups = [];
            }

            // Get the combinations set for this layer
            const layerCombinations = symbologyCombinations[layer.id];

            if (
              layerCombinations &&
              layer.layerDefinition.drawingInfo.renderer.uniqueValueInfos
            ) {
              if (dataPage?.includes("A0")) {
                if (
                  Array.isArray(
                    layer.layerDefinition.drawingInfo.renderer.visualVariables
                  )
                ) {
                  layer.layerDefinition.drawingInfo.renderer.visualVariables.forEach(
                    (vv) => {
                      if (Array.isArray(vv.stops)) {
                        vv.stops.forEach((stop) => {
                          stop.size = stop.size * 2;
                        });
                      }
                    }
                  );
                }
              }

              // Filter uniqueValueInfos to keep only items with values in the combinations set
              layer.layerDefinition.drawingInfo.renderer.uniqueValueInfos =
                layer.layerDefinition.drawingInfo.renderer.uniqueValueInfos.filter(
                  (info) => {
                    const value = info.value;

                    // Check if the value is a range
                    if (typeof value === "string" && value.includes("-")) {
                      const [min, max] = value.split("-").map(Number); // Parse the range
                      if (!isNaN(min) && !isNaN(max)) {
                        // Check if any value in the set falls within the range
                        for (const setValue of layerCombinations) {
                          const numericSetValue = Number(setValue);
                          if (
                            !isNaN(numericSetValue) &&
                            numericSetValue >= min &&
                            numericSetValue <= max
                          ) {
                            return true;
                          }
                        }
                      }
                      return false; // No matching value in the range
                    }

                    // For non-range values, check if the set contains the value
                    return layerCombinations.has(value);
                  }
                );

              if (layer.layerDefinition.drawingInfo.renderer.defaultSymbol) {
                const defaultSymbolBase64 = defaultMap.get(layer.id);
                if (defaultSymbolBase64) {
                  try {
                    // Check if the input is a data URL
                    let pngString = await convertSvgBase64ToPng(
                      defaultSymbolBase64
                    );

                    if (pngString.startsWith("data:")) {
                      // Extract the base64 portion after the comma
                      pngString = pngString.split(",")[1];
                    }

                    layer.layerDefinition.drawingInfo.renderer.defaultSymbol.imageData =
                      pngString;
                  } catch (error) {
                    console.log(error);
                  }
                }
              }

              for (const info of layer.layerDefinition.drawingInfo.renderer
                .uniqueValueInfos) {
                let iconBase64 = symbolMap.get(info.description);
                if (iconBase64) {
                  try {
                    // Check if the input is a data URL
                    let pngString = await convertSvgBase64ToPng(iconBase64);

                    if (pngString.startsWith("data:")) {
                      // Extract the base64 portion after the comma
                      pngString = pngString.split(",")[1];
                    }

                    info.symbol.imageData = pngString;
                  } catch (error) {
                    console.log(error);
                  }
                }
              }
            }
          }

          if (layer.id === "locationsGraphicsLayer" && locationsGraphicsLayer) {
            try {
              // Get the scale map from the print mask attribute
              const scaleMapJson =
                printMask?.getAttribute("data-scale-map") || "{}";
              let retrievedScaleMap;

              // Parse the JSON string into a Map object
              const scaleMapEntries = JSON.parse(scaleMapJson);
              retrievedScaleMap = new Map(scaleMapEntries);

              // Apply the scales to each layer
              view.map.allLayers.forEach((l) => {
                if (retrievedScaleMap.has(l.id)) {
                  // Get the scale for this layer ID
                  const scale = retrievedScaleMap.get(l.id);

                  // Apply the scale to the layer
                  if (typeof scale === "number" || scale === 0) {
                    l.minScale = scale;
                  }
                }
              });

              if (printMask) {
                // After applying all scales, reset the data-scale-map attribute to empty
                printMask.setAttribute("data-scale-map", "{}");
              }
            } catch (error) {
              console.warn("Failed to parse scale map data:", error);
            }

            const features = [];

            locationsGraphicsLayer.graphics.forEach((g) => {
              const printGraphic = JSON.parse(JSON.stringify(g));
              const { attributes, ...rest } = printGraphic;
              features.push(rest);
            });

            if (
              Array.isArray(layer?.featureCollection?.layers) &&
              layer.featureCollection.layers.length > 0
            ) {
              layer.featureCollection.layers[0].featureSet.features =
                features.sort((a, b) => {
                  const fontSizeA = a.symbol.font.size;
                  const fontSizeB = b.symbol.font.size;
                  return fontSizeA - fontSizeB; // Small fonts first, large fonts last
                });
            }
          }
        }

        const mapExtent = view.extent;
        const map = document.querySelector("#pdfMap");
        let topLeft, bottomRight;

        const mapPos = map.getBoundingClientRect();
        if (mapPos.x < 260 && dataPage.includes("Landscape")) {
          topLeft = view.toMap({
            x: mapPos.x === 114 ? 114 : mapPos.x,
            y: dataPage.includes("Landscape") ? 10 : 65,
          });
          bottomRight = view.toMap({
            x: (mapPos.x === 114 ? 114 : mapPos.x) + map.offsetWidth,
            y: map.offsetHeight + (dataPage.includes("Landscape") ? 10 : 65),
          });
        } else {
          const leftMaskWidth = (screen.offsetWidth - map.offsetWidth) / 2;
          //We cut the sides
          const printWidthPx = (screen.offsetHeight - 62) * printoutRatio;

          topLeft = view.toMap({
            x: leftMaskWidth,
            y: dataPage.includes("Landscape") ? 10 : 65,
          });
          bottomRight = view.toMap({
            x: leftMaskWidth + map.offsetWidth,
            y: screen.offsetHeight - 58,
          });
        }

        mapExtent.xmin = topLeft.x;
        mapExtent.xmax = bottomRight.x;
        mapExtent.ymin = topLeft.y;
        mapExtent.ymax = bottomRight.y;

        dispatch(setPrintWidgetLoading(true));
        view.map.allLayers.forEach((layer) => {
          const wasVisible = allLayersVisibilityMap.get(layer.id);
          const isVisible = layer.visible;
          if (wasVisible !== isVisible) {
            layer.visible = wasVisible;
          }
        });
        jsonToEdit.exportOptions["dpi"] = dpi;

        jsonToEdit.mapOptions.extent = mapExtent.toJSON();
        jsonToEdit.mapOptions.scale = undefined;

        params.requestOptions.query["Web_Map_as_JSON"] =
          JSON.stringify(jsonToEdit);
        params.requestOptions.query["Layout_Template"] = value;
      },
      after: function (params) {
        // console.log("AFTER", params);
      },
      error: function (params) {
        console.log("ERROR", params);
      },
    });
  }, []);

  // Serialize combinations for storage/transport
  const serializeCombinations = (combinations) => {
    const serialized = {};
    Object.entries(combinations).forEach(([layerId, set]) => {
      serialized[layerId] = [...set].join("|");
    });

    return JSON.stringify(serialized);
  };

  // Deserialize back to Set objects
  const deserializeCombinations = (json) => {
    const serialized = JSON.parse(json);
    const combinations = {};
    Object.entries(serialized).forEach(([layerId, str]) => {
      combinations[layerId] = new Set(str.split("|"));
    });
    return combinations;
  };

  return ReactDOM.createPortal(
    <StyledPrintMask
      id="print-mask"
      data-combinations={serializeCombinations(symbologyVariations)}
      data-scale-map=""
      isLoading={printWidget.isLoading}
      className="printWidgetMask"
    >
      <div
        className={
          masks.type === "horizontal"
            ? "mask horizontalMask leftMask"
            : "mask verticalMask topMask"
        }
        style={masks.style}
      />
      <div
        className={
          masks.type === "horizontal"
            ? "mask horizontalMask rightMask"
            : "mask verticalMask bottomMask"
        }
        style={masks.style}
      />
      <div
        style={{
          width: masks.width ?? "100%",
          left: masks.style.width,
          top: 0,
          height: 10,
          background: "#32323280",
          position: "absolute",
        }}
      />
      <div
        style={{
          width: masks.width ?? "100%",
          left: masks.style.width,
          bottom: 0,
          height: masks.height ? parent.offsetHeight - (masks.height + 10) : 10,
          background: "#32323280",
          position: "absolute",
        }}
      />
      <PrintScreen
        config={config}
        parent={parent}
        setPage={setPage}
        page={page}
        setDpi={setDpi}
        dpi={dpi}
        masks={masks}
        t={t}
        i18n={i18n}
        setSymbologyCombinations={setSymbologyCombinations}
      />
    </StyledPrintMask>,
    document.body
  );
};

export default PrintMask;
